import { ConsultTranscriptionDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import {
    executeQuery,
    executeMutation,
    fetchVersion,
} from 'app/api/client/graphqlClient'
import {
    consultTranscriptionDtosByConsultId,
    createConsultTranscriptionDtoMutation,
    updateConsultTranscriptionDtoMutation,
    deleteConsultTranscriptionDtoMutation,
} from 'app/graphql/customQueries'
import { isNotDeleted } from './softDelete'

/**
 * Repository interface for managing transcription data.
 * Provides CRUD operations for ConsultTranscriptionDto records.
 */
export interface TranscriptionRepository {
    /**
     * Create a new transcription record for a consult.
     * @param consultId - The ID of the consult
     * @param clientTranscription - The client-side transcription text
     * @param cloudTranscription - The cloud transcription text (optional)
     * @returns The created ConsultTranscriptionDto
     */
    create(
        consultId: string,
        clientTranscription: string,
        cloudTranscription?: string | null
    ): Promise<ConsultTranscriptionDto>

    /**
     * Get transcription record by consult ID.
     * @param consultId - The ID of the consult
     * @returns The ConsultTranscriptionDto or null if not found
     */
    getByConsultId(consultId: string): Promise<ConsultTranscriptionDto | null>

    /**
     * Update transcription for a consult.
     * Creates a new record if one doesn't exist.
     * @param consultId - The ID of the consult
     * @param clientTranscription - The client-side transcription text
     * @param cloudTranscription - The cloud transcription text (optional)
     * @returns The updated ConsultTranscriptionDto
     */
    update(
        consultId: string,
        clientTranscription: string,
        cloudTranscription?: string | null
    ): Promise<ConsultTranscriptionDto>

    /**
     * Delete transcription record for a consult.
     * @param consultId - The ID of the consult
     */
    delete(consultId: string): Promise<void>
}

interface ConsultTranscriptionDtoResult {
    id: string
    consultId: string
    owner?: string
    clientTranscription: string
    cloudTranscription?: string | null
}

interface ConsultTranscriptionDtosByConsultIdResult {
    items: ConsultTranscriptionDtoResult[]
    nextToken: string | null
}

/**
 * GraphQL API implementation of TranscriptionRepository.
 * Manages transcription data via AppSync GraphQL operations.
 */
export class AmplifyTranscriptionRepository implements TranscriptionRepository {
    async create(
        consultId: string,
        clientTranscription: string,
        cloudTranscription?: string | null
    ): Promise<ConsultTranscriptionDto> {
        try {
            logger.debug('Creating transcription record', {
                action: 'create_transcription',
                metadata: {
                    consultId,
                    clientLength: clientTranscription.length,
                    hasCloud: !!cloudTranscription,
                },
            })

            const created = await executeMutation<ConsultTranscriptionDto>(
                createConsultTranscriptionDtoMutation,
                {
                    input: {
                        consultId,
                        clientTranscription,
                        cloudTranscription: cloudTranscription ?? null,
                    },
                },
                'createConsultTranscriptionDto'
            )

            logger.info('Transcription record created successfully', {
                action: 'create_transcription_success',
                metadata: {
                    consultId,
                    transcriptionId: created.id,
                    clientLength: clientTranscription.length,
                    hasCloud: !!cloudTranscription,
                },
            })

            return created
        } catch (error) {
            logger.error(
                'Failed to create transcription record',
                error as Error,
                {
                    action: 'create_transcription_error',
                    metadata: { consultId },
                }
            )
            throw error
        }
    }

    async getByConsultId(
        consultId: string
    ): Promise<ConsultTranscriptionDto | null> {
        try {
            logger.debug('Querying transcription by consultId', {
                action: 'get_transcription',
                metadata: { consultId },
            })

            const result =
                await executeQuery<ConsultTranscriptionDtosByConsultIdResult>(
                    consultTranscriptionDtosByConsultId,
                    { consultId },
                    'consultTranscriptionDtosByConsultId'
                )

            const items = result.items.filter(isNotDeleted)

            if (items.length === 0) {
                logger.debug('No transcription found for consult', {
                    action: 'get_transcription_not_found',
                    metadata: { consultId },
                })
                return null
            }

            if (items.length > 1) {
                logger.warn(
                    'Multiple transcription records found for consult',
                    {
                        action: 'get_transcription_multiple',
                        metadata: { consultId, count: items.length },
                    }
                )
            }

            return items[0] as unknown as ConsultTranscriptionDto
        } catch (error) {
            logger.error('Failed to query transcription', error as Error, {
                action: 'get_transcription_error',
                metadata: { consultId },
            })
            throw error
        }
    }

    async update(
        consultId: string,
        clientTranscription: string,
        cloudTranscription?: string | null
    ): Promise<ConsultTranscriptionDto> {
        try {
            const existing = await this.getByConsultId(consultId)

            if (!existing) {
                logger.debug('No existing transcription, creating new', {
                    action: 'update_transcription_create',
                    metadata: { consultId },
                })
                return this.create(
                    consultId,
                    clientTranscription,
                    cloudTranscription
                )
            }

            const _version = await fetchVersion(
                'ConsultTranscriptionDto',
                existing.id
            )

            const updated = await executeMutation<ConsultTranscriptionDto>(
                updateConsultTranscriptionDtoMutation,
                {
                    input: {
                        id: existing.id,
                        clientTranscription,
                        cloudTranscription: cloudTranscription ?? null,
                        _version,
                    },
                },
                'updateConsultTranscriptionDto'
            )

            logger.info('Transcription record updated successfully', {
                action: 'update_transcription_success',
                metadata: {
                    consultId,
                    clientLength: clientTranscription.length,
                    hasCloud: !!cloudTranscription,
                },
            })

            return updated
        } catch (error) {
            logger.error('Failed to update transcription', error as Error, {
                action: 'update_transcription_error',
                metadata: { consultId },
            })
            throw error
        }
    }

    async delete(consultId: string): Promise<void> {
        try {
            const existing = await this.getByConsultId(consultId)

            if (!existing) {
                logger.debug('No transcription to delete', {
                    action: 'delete_transcription_not_found',
                    metadata: { consultId },
                })
                return
            }

            const _version = await fetchVersion(
                'ConsultTranscriptionDto',
                existing.id
            )

            await executeMutation(
                deleteConsultTranscriptionDtoMutation,
                {
                    input: {
                        id: existing.id,
                        _version,
                    },
                },
                'deleteConsultTranscriptionDto'
            )

            logger.info('Transcription record deleted successfully', {
                action: 'delete_transcription_success',
                metadata: { consultId },
            })
        } catch (error) {
            logger.error('Failed to delete transcription', error as Error, {
                action: 'delete_transcription_error',
                metadata: { consultId },
            })
            throw error
        }
    }
}
